import React, { useRef, useEffect } from 'react';
import * as echarts from 'echarts';
import { ze } from 'utils/zhEn';
import { CS_STYLES } from 'utils/constants/style-constants';

const JankDiffFlameGraph = ({ baseData, currentData, threshold = 5 }) => {
  const chartRef = useRef(null);

  // 颜色编码配置
  const diffColorMap = {
    added: CS_STYLES.LINK_COLOR,    // 新增节点
    removed: CS_STYLES.PRIMARY_COLOR_RED,  // 被移除节点
    changed: CS_STYLES.DIFF_FLAME_GRAPH_CHANGED,  // 显著变化
    unchanged: CS_STYLES.DIFF_FLAME_GRAPH_UNCHANGED // 稳定节点
  };

  useEffect(() => {
    if (!baseData || !currentData) return;

    // 1. 计算差分树
    const diffTree = buildDiffTree(baseData, currentData, threshold);

    // 2. 准备ECharts数据
    const flameData = prepareFlameData(diffTree);

    // 3. 渲染图表
    renderChart(flameData, diffTree.value);

    return () => {
      if (chartRef.current) {
        echarts.dispose(chartRef.current);
      }
    };
  }, [baseData, currentData, threshold]);

  // 构建差分树
  const buildDiffTree = (base, current, threshold) => {
    if (!base && !current) return null;

    const node = {
      name: current?.name || base?.name,
      baseValue: base?.value || 0,
      currentValue: current?.value || 0,
      diffType: 'unchanged',
      children: []
    };

    // 判断节点差异类型
    if (!base && current) {
      node.diffType = 'added';
    } else if (base && !current) {
      node.diffType = 'removed';
    } else {
      const changePercent = Math.abs((current.value - base.value) / base.value * 100);
      node.diffType = changePercent > threshold ? 'changed' : 'unchanged';
    }

    // 处理子节点
    const baseChildren = base?.children || [];
    const currentChildren = current?.children || [];
    const baseMap = new Map(baseChildren.map(c => [c.name, c]));
    const currentMap = new Map(currentChildren.map(c => [c.name, c]));

    // 对比所有子节点
    const allNames = new Set([...baseMap.keys(), ...currentMap.keys()]);
    for (const name of allNames) {
      const childDiff = buildDiffTree(
        baseMap.get(name),
        currentMap.get(name),
        threshold
      );
      if (childDiff) node.children.push(childDiff);
    }

    // 计算当前节点的总值（取较大值）
    node.value = Math.max(
      node.children.reduce((sum, c) => sum + c.baseValue, 0),
      node.children.reduce((sum, c) => sum + c.currentValue, 0)
    ) || node.currentValue || node.baseValue;

    return node;
  };

  // 准备火焰图数据
  const prepareFlameData = (diffTree) => {
    const flameData = [];
    const maxLevel = getTreeDepth(diffTree);
    const rootValue = diffTree.value;

    const recur = (node, start = 0, level = 0) => {
      const value = Math.max(node.baseValue, node.currentValue);
      const color = diffColorMap[node.diffType];

      flameData.push({
        name: node.name,
        value: [
          level,
          start,
          start + value,
          node.name,
          (value / rootValue) * 100,
          node.diffType,
          node.baseValue,
          node.currentValue
        ],
        itemStyle: { color },
        diffType: node.diffType
      });

      let childStart = start;
      for (const child of node.children || []) {
        recur(child, childStart, level + 1);
        childStart += Math.max(child.baseValue, child.currentValue);
      }
    };

    recur(diffTree);
    return { data: flameData, maxLevel };
  };

  // 获取树深度
  const getTreeDepth = (node) => {
    if (!node.children || node.children.length === 0) return 1;
    return 1 + Math.max(...node.children.map(getTreeDepth));
  };

  // 渲染图表
  const renderChart = (flameData, rootValue) => {
    const { data, maxLevel } = flameData;
    const myChart = echarts.init(chartRef.current);

    const renderItem = (params, api) => {
      const level = api.value(0);
      const start = api.coord([api.value(1), level]);
      const end = api.coord([api.value(2), level]);
      const height = ((api.size && api.size([0, 1])) || [0, 20])[1];
      const width = end[0] - start[0];

      return {
        type: 'rect',
        shape: {
          x: start[0],
          y: start[1] - height / 2,
          width,
          height: height - 2,
          r: 2
        },
        style: {
          fill: api.visual('color')
        },
        emphasis: {
          style: {
            stroke: '#000'
          }
        },
        textConfig: {
          position: 'insideLeft'
        },
        textContent: {
          style: {
            text: api.value(3),
            fontFamily: 'Verdana',
            fill: '#000',
            width: width - 4,
            overflow: 'truncate',
            ellipsis: '..'
          }
        }
      };
    };

    const option = {
      tooltip: {
        formatter: (params) => {
          const diffType = params.value[5];
          const baseVal = params.value[6];
          const currentVal = params.value[7];
          const change = currentVal - baseVal;
          const changePercent = baseVal ?
            (Math.abs(change) / baseVal * 100).toFixed(2) : '∞';

          return `
            <div style="font-weight:bold">${params.value[3]}</div>
            <div>
              ${ze('类型','type')}: ${diffType === 'added' ? ze('新增节点','added') :
                diffType === 'removed' ? ze('被移除节点','removed') :
                diffType === 'changed' ? ze('显著变化','regressed') : ze('稳定节点','unchanged')}
            </div>
            <div>${ze('基准值','baselineValue')}: ${baseVal.toFixed(2)}ms</div>
            <div>${ze('当前值','currentValue')}: ${currentVal.toFixed(2)}ms</div>
            ${diffType !== 'added' && diffType !== 'removed' ? `
              <div>${ze('差值','diffValue')}: ${change.toFixed(2)}ms (${changePercent}%)</div>
            ` : ''}
          `;
        }
      },
      grid: {
        top: 25,
        bottom: 20,
        left: 20,
        right: 20
      },
      xAxis: {
        show: false,
        max: rootValue,
        min: 0,
      },
      yAxis: {
        show: false,
        max: maxLevel,
        min: 0,
        inverse: false,
      },
      series: [{
        type: 'custom',
        renderItem,
        encode: {
          x: [1, 2],
          y: 0
        },
        data: data,
        animation: false
      }]
    };

    myChart.setOption(option);
  };

  const colorMeaningList = Object.entries(diffColorMap).map(([key, color]) => {
    const meaning = key === 'added'? ze('新增节点','added') :
                      key ==='removed'? ze('被移除节点','removed') :
                      key === 'changed'? ze('显著变化','regressed') : ze('稳定节点','unchanged');
    return (
      <span key={key} style={{ display: 'flex', alignItems: 'center', marginRight:'10px' }}>
        <span style={{ display: 'inline-block', width: 20, height: 15, backgroundColor: color }} />
        <span style={{ marginLeft: 5 }}>{meaning}</span>
      </span>
    );
  });

  return (
    <div style={{overflow:'hidden'}}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
        {colorMeaningList}
      </div>
      <div
        ref={chartRef}
        style={{
          width: '100%',
          height: '300px',
          borderRadius: '4px'
        }}
      />
    </div>
  );
};

export default JankDiffFlameGraph;